import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ColorThief from "colorthief";
import { getProfile } from "../../api/mainApi";

function Achievement() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Спочатку пробуємо взяти з localStorage (для зворотної сумісності), але за замовчуванням null
  const [achievement, setAchievement] = useState(
    localStorage.getItem("achievement") || null
  );

  const [bgColor, setBgColor]   = useState("#1a202c");
  const [textColor, setTextColor] = useState("#fff");
  const [loading, setLoading]   = useState(true);
  const imgRef = useRef(null);

  /* ─── 1. Завантаження досягнень через API або очищення localStorage ─── */
  useEffect(() => {
    // Якщо прийшли через збереження настрою (в localStorage вже щось є)
    if (achievement) {
      localStorage.removeItem("achievement");
      // setLoading залишається true, чекаємо handleImageLoad від картинки
      return;
    }

    // Якщо прийшли по Deep Link (в localStorage порожньо) — робимо запит до профілю
    const fetchLatestAchievement = async () => {
      try {
        const profile = await getProfile();
        
        if (!profile || !profile.achievements) {
          navigate("/");
          return;
        }

        // Відсікаємо всі false, залишаємо тільки true і сортуємо за зростанням днів
        // Об'єкт { day1: true, day7: true } -> масив [['day1', true], ['day7', true]]
        const unlockedAchievements = Object.entries(profile.achievements)
          .filter(([_, achieved]) => achieved === true)
          .sort((a, b) => {
            const daysA = parseInt(a[0].replace("day", ""), 10);
            const daysB = parseInt(b[0].replace("day", ""), 10);
            return daysA - daysB;
          });

        if (unlockedAchievements.length === 0) {
          // Якщо немає жодного досягнення із значенням true
          navigate("/");
          return;
        }

        // Беремо останнє (найсвіжіше/найбільше) розблоковане досягнення
        const latestAchievementKey = unlockedAchievements[unlockedAchievements.length - 1][0];
        setAchievement(latestAchievementKey);
      } catch (err) {
        console.error("Error fetching achievement via getProfile:", err);
        navigate("/");
      }
    };

    fetchLatestAchievement();
  }, [navigate]); // Виконується один раз при монтуванні компонента

  /* ─── 2. Отримати колір зображення ────────────────── */
  const handleImageLoad = () => extractColor();

  const extractColor = () => {
    const img = imgRef.current;
    if (!img) return;
    try {
      const colorThief   = new ColorThief();
      const dominant     = colorThief.getColor(img);
      setBgColor(`rgb(${dominant[0]}, ${dominant[1]}, ${dominant[2]})`);

      const darker       = dominant.map((c) => Math.floor(c * 0.6));
      setTextColor(`rgb(${darker[0]}, ${darker[1]}, ${darker[2]})`);
    } catch (err) {
      console.error("Error extracting dominant color:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ─── 3. Підготовка даних для шаблону ─────────────── */
  const streak = Number(String(achievement).replace(/\D+/g, "")) || 0;

  /* ─── 4. Навігація далі ───────────────────────────── */
  const handleContinue = () => navigate("/");

  // Захист: поки немає назви досягнення і триває завантаження профілю — показуємо тільки спінер
  if (!achievement) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 bg-gray-900 h-screen">
        <div className="animate-spin rounded-full h-24 w-24 border-t-4 border-b-4 border-white"></div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col -m-4 items-center justify-center flex-1 transition-colors duration-300 min-h-screen"
      style={{ backgroundColor: bgColor }}
    >
      <div className="flex-1 flex flex-col justify-center relative">
        <div className="relative mx-auto w-3/4">
          {/* spinner */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="animate-spin rounded-full h-24 w-24 border-t-4 border-b-4 border-white"></div>
            </div>
          )}

          {/* зображення */}
          <img
            ref={imgRef}
            src={`achievement_${achievement}.webp`}
            crossOrigin="anonymous"
            onLoad={handleImageLoad}
            className={`relative z-10 animate-shake transition-opacity duration-300 ${
              loading ? "opacity-0" : "opacity-100"
            }`}
            alt="Achievement"
          />

          {/* світіння */}
          {!loading && (
            <div className="absolute inset-0 animate-pulse rounded-full filter blur-3xl bg-white opacity-40"></div>
          )}
        </div>

        {/* текст */}
        {!loading && (
          <p className="text-2xl mt-4 font-bold mx-8 text-center" style={{ color: textColor }}>
            {t("achievement.streakText", { count: streak })}
          </p>
        )}
      </div>

      {/* кнопка */}
      {!loading && (
        <button
          onClick={handleContinue}
          className="text-3xl my-6 font-semibold px-6 py-2 w-full mx-8 transition-colors duration-300 uppercase"
          style={{ color: textColor }}
        >
          {t("achievement.continue")}
        </button>
      )}
    </div>
  );
}

export default Achievement;